import { liveQuery } from "dexie";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";

import { getGatewayBaseUrl } from "../runtime/config";
import {
  PosSalesHistoryGatewayClient,
  type PosOrderDetailResponse,
  type PosReceivableBusinessState,
  type PosSalesHistoryOrder,
  type PosSalesHistoryResponse,
} from "../runtime/gateway/sales-history-client";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import {
  commitLocalSaleVoid,
  readPendingSaleVoidOrderIds,
  syncPendingSaleVoids,
} from "../runtime/local-store/sale-void-state";
import {
  readSalesHistory,
  readSalesOrderDetail,
  saveSalesHistory,
  saveSalesOrderDetail,
} from "../runtime/local-store/secondary-runtime-cache";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";

type RuntimeSnapshot = {
  terminal: TerminalCredentialState | null;
  session: LocalEmployeeSessionState | null;
};

type VoidKind = "CASH" | "CREDIT";

type VoidReviewTarget = {
  orderId: string;
  orderNo: string;
  amount: number;
  currencyCode: string;
  kind: VoidKind;
};

const PAGE_SIZE = 50;

function money(value: number | null | undefined, currency = "THB") {
  return new Intl.NumberFormat("th-TH", { style: "currency", currency, minimumFractionDigits: 2 }).format(Number(value ?? 0));
}

function dateTime(value: string | null | undefined, timezone?: string) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("th-TH", { dateStyle: "short", timeStyle: "short", timeZone: timezone }).format(new Date(value));
}

function buddhistBusinessDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "—";
  const [year, month, day] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("th-TH-u-ca-buddhist-nu-latn", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    PAID: "ชำระแล้ว",
    VOIDED: "ยกเลิกบิล",
    REFUNDED: "คืนเงินแล้ว",
    PARTIALLY_REFUNDED: "คืนเงินบางส่วน",
    FULFILLED: "สำเร็จ",
    CANCELLED: "ยกเลิก",
  };
  return labels[status] ?? status.split("_").join(" ");
}

function receivableStatusLabel(receivable: PosReceivableBusinessState) {
  const labels: Record<string, string> = {
    OPEN: "ค้างชำระ",
    PARTIALLY_PAID: "ชำระบางส่วน",
    PAID: "ชำระแล้ว",
    VOIDED: "ยกเลิกบิล",
  };
  return labels[receivable.receivable_status] ?? "รายการค้างชำระ";
}

function businessStatusLabel(orderStatus: string, receivable?: PosReceivableBusinessState | null) {
  if (orderStatus === "VOIDED") return "ยกเลิกบิล";
  if (receivable) return receivableStatusLabel(receivable);
  return statusLabel(orderStatus);
}

function businessStatusCode(orderStatus: string, receivable?: PosReceivableBusinessState | null) {
  if (orderStatus === "VOIDED") return "VOIDED";
  return receivable?.receivable_status ?? orderStatus;
}

function paymentLabel(order: PosSalesHistoryOrder) {
  if (order.receivable) return "ชำระภายหลัง";
  if (!order.payments.length) return "ยังไม่มีข้อมูลการชำระเงิน";
  return order.payments.map((payment) => payment.payment_method_name || payment.payment_method_code).join(" + ");
}

function cashTenderAudit(order: PosSalesHistoryOrder) {
  if (order.receivable) return null;
  const payment = order.payments.find((candidate) => candidate.payment_method_code === "CASH" && candidate.tendered_amount != null);
  if (!payment) return null;
  return {
    tenderedAmount: payment.tendered_amount,
    changeAmount: payment.change_amount,
  };
}

function itemLabel(order: PosSalesHistoryOrder) {
  if (!order.items.length) return "ไม่มีรายละเอียดสินค้า";
  const first = order.items[0];
  return order.items.length > 1 ? `${first.item_name} × ${first.quantity} · +${order.items.length - 1}` : `${first.item_name} × ${first.quantity}`;
}

function requestKey(orderId: string, kind: VoidKind) {
  const suffix = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `pos-history-${kind.toLowerCase()}-void:${orderId}:${suffix}`;
}

function isConnectivityError(value: string | null | undefined) {
  if (!value) return false;
  return value.includes("GATEWAY_TIMEOUT") || value.includes("GATEWAY_UNREACHABLE") || value.includes("POS_GATEWAY_NOT_CONFIGURED");
}

function friendlyError(value: string) {
  if (value.includes("POS_SALES_HISTORY_PERMISSION_REQUIRED")) return "พนักงานคนนี้ไม่มีสิทธิ์ดูประวัติการขาย";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "เซสชันพนักงานหมดอายุ กรุณา Login ใหม่";
  if (value.includes("POS_CASH_VOID_REQUIRES_CASH_SALE_V1")) return "บิลนี้ไม่ได้ชำระด้วยเงินสด จึงยกเลิกด้วยวิธีเงินสดไม่ได้";
  if (value.includes("POS_CREDIT_VOID_REQUIRES_CREDIT_SALE_V1")) return "บิลนี้ไม่ใช่รายการชำระภายหลัง จึงยกเลิกด้วยวิธีนี้ไม่ได้";
  if (value.includes("POS_CASH_VOID_REQUIRES_ORIGINAL_EMPLOYEE_V1") || value.includes("POS_CREDIT_VOID_REQUIRES_ORIGINAL_EMPLOYEE_V1")) return "บิลนี้ยกเลิกได้เฉพาะพนักงานคนเดิมที่ขายบิล";
  if (value.includes("POS_CASH_VOID_ORIGINAL_SHIFT_NOT_OPEN") || value.includes("POS_CREDIT_VOID_ORIGINAL_SHIFT_NOT_OPEN")) return "กะเดิมของบิลนี้ปิดแล้ว จึงยกเลิกจาก POS ไม่ได้";
  if (value.includes("POS_CASH_VOID_CLAIM_REVERSAL_NOT_SUPPORTED_V1") || value.includes("POS_CREDIT_VOID_CLAIM_REVERSAL_NOT_SUPPORTED_V1")) return "บิลนี้มีการใช้คูปองแล้ว จึงยกเลิกจากหน้านี้ไม่ได้";
  if (value.includes("POS_CREDIT_VOID_REQUIRES_UNALLOCATED_RECEIVABLE") || value.includes("POS_CREDIT_VOID_REQUIRES_UNSETTLED_ORDER") || value.includes("POS_CREDIT_VOID_PAYMENT_MUST_NOT_EXIST")) return "รายการชำระภายหลังนี้มีการรับชำระแล้ว จึงยกเลิกแบบค้างชำระไม่ได้";
  if (value.includes("POS_CREDIT_VOID_REQUIRES_OPEN_RECEIVABLE")) return "รายการค้างชำระนี้ไม่อยู่ในสถานะที่ยกเลิกได้";
  if (value.includes("PERMISSION_REQUIRED")) return "พนักงานคนนี้ไม่มีสิทธิ์ทำรายการนี้";
  if (isConnectivityError(value)) return "ยกเลิกบิลแล้ว ระบบจะอัปเดตข้อมูลให้อัตโนมัติเมื่อเชื่อมต่อได้";
  if (/^[A-Z0-9_]+$/.test(value)) return "ทำรายการไม่สำเร็จ กรุณาลองอีกครั้ง";
  return value || "ทำรายการไม่สำเร็จ";
}

export function SalesHistoryRuntimeHost() {
  const [runtime, setRuntime] = useState<RuntimeSnapshot>({ terminal: null, session: null });
  const [mountNode, setMountNode] = useState<Element | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<PosSalesHistoryResponse | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [businessDate, setBusinessDate] = useState("");
  const [draftSearch, setDraftSearch] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PosOrderDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const detailRequestId = useRef(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [voidKind, setVoidKind] = useState<VoidKind | null>(null);
  const [voidTarget, setVoidTarget] = useState<VoidReviewTarget | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidRequestKey, setVoidRequestKey] = useState("");
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voidNotice, setVoidNotice] = useState<string | null>(null);
  const [pendingVoidOrderIds, setPendingVoidOrderIds] = useState<string[]>([]);
  const voidSyncingRef = useRef(false);

  useEffect(() => {
    const subscription = liveQuery(async () => ({
      terminal: await readTerminalCredential(),
      session: await readLocalEmployeeSession(),
    })).subscribe({ next: setRuntime, error: () => setRuntime({ terminal: null, session: null }) });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const updateMount = () => setMountNode(document.querySelector(".sale-header__actions"));
    updateMount();
    const observer = new MutationObserver(updateMount);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => { window.removeEventListener("online", update); window.removeEventListener("offline", update); };
  }, []);

  const terminal = runtime.terminal;
  const session = runtime.session;
  const actor = session?.actor ?? null;
  const identity = terminal?.identity ?? null;
  const sameScope = Boolean(actor && identity && actor.organization_id === identity.organization_id && actor.branch_id === identity.branch_id && actor.node_id === identity.node_id && actor.runtime_session_status === "ACTIVE");
  const canView = Boolean(sameScope && actor?.permission_codes.includes("commerce.view"));
  const baseUrl = getGatewayBaseUrl();

  useEffect(() => {
    if (!identity) {
      setPendingVoidOrderIds([]);
      return;
    }
    const subscription = liveQuery(() => readPendingSaleVoidOrderIds(identity)).subscribe({
      next: setPendingVoidOrderIds,
      error: () => setPendingVoidOrderIds([]),
    });
    return () => subscription.unsubscribe();
  }, [identity?.organization_id, identity?.branch_id, identity?.node_id]);

  async function loadHistory(nextOffset = offset, nextDate = businessDate, nextSearch = search) {
    if (!terminal || !session || !identity) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const cached = await readSalesHistory(identity, nextDate || null, nextSearch || null, PAGE_SIZE, nextOffset);
      if (cached) {
        setHistory(cached);
        if (!nextDate) setBusinessDate(cached.business_date);
        setOffset(nextOffset);
      }

      if (!baseUrl || !online) return;
      const client = new PosSalesHistoryGatewayClient({ baseUrl });
      const response = await client.list({
        deviceSecret: terminal.deviceSecret,
        sessionToken: session.sessionToken,
        businessDate: nextDate || null,
        search: nextSearch || null,
        limit: PAGE_SIZE,
        offset: nextOffset,
      });
      if (!response.ok || !response.data) throw new Error(response.error ?? "POS_SALES_HISTORY_LOAD_FAILED");
      await saveSalesHistory(identity, response.data);
      setHistory(response.data);
      setBusinessDate(response.data.business_date);
      setOffset(nextOffset);
    } catch (error) {
      setHistoryError(friendlyError(error instanceof Error ? error.message : "POS_SALES_HISTORY_LOAD_FAILED"));
    } finally {
      setHistoryLoading(false);
    }
  }

  async function loadDetail(orderId: string) {
    if (!terminal || !session || !identity) return;
    const requestId = ++detailRequestId.current;
    setSelectedOrderId(orderId);
    setDetailLoading(true);
    setDetailError(null);
    setVoidNotice(null);
    try {
      const cached = await readSalesOrderDetail(identity, orderId);
      if (requestId !== detailRequestId.current) return;
      if (cached) setDetail(cached);

      if (!baseUrl || !online) return;
      const client = new PosSalesHistoryGatewayClient({ baseUrl });
      const response = await client.detail({ deviceSecret: terminal.deviceSecret, sessionToken: session.sessionToken, orderId });
      if (!response.ok || !response.data) throw new Error(response.error ?? "POS_ORDER_DETAIL_LOAD_FAILED");
      await saveSalesOrderDetail(identity, response.data);
      if (requestId === detailRequestId.current) setDetail(response.data);
    } catch (error) {
      if (requestId === detailRequestId.current) {
        setDetailError(friendlyError(error instanceof Error ? error.message : "POS_ORDER_DETAIL_LOAD_FAILED"));
      }
    } finally {
      if (requestId === detailRequestId.current) setDetailLoading(false);
    }
  }

  useEffect(() => {
    if (!online || !terminal || !session || !identity || !baseUrl || voidSyncingRef.current) return;
    let cancelled = false;
    voidSyncingRef.current = true;
    void syncPendingSaleVoids(identity, terminal.deviceSecret, session.sessionToken)
      .then(async (results) => {
        if (cancelled || !results.length) return;
        const failure = results.find((result) => !result.ok && !isConnectivityError(result.error));
        if (failure && failure.orderId === selectedOrderId) {
          setVoidNotice(friendlyError(failure.error ?? "SALE_VOID_SYNC_FAILED"));
        }
        if (results.some((result) => result.ok)) {
          setVoidNotice("ยกเลิกบิลแล้ว");
          if (open) {
            if (selectedOrderId) await loadDetail(selectedOrderId);
            await loadHistory(offset, businessDate, search);
          }
        }
      })
      .finally(() => { voidSyncingRef.current = false; });
    return () => { cancelled = true; };
  }, [online, terminal?.deviceSecret, session?.sessionToken, identity?.organization_id, identity?.branch_id, identity?.node_id, baseUrl, open, selectedOrderId]);

  function openHistory() {
    detailRequestId.current += 1;
    setOpen(true);
    setSelectedOrderId(null);
    setDetail(null);
    setSearch("");
    setDraftSearch("");
    setOffset(0);
    setVoidNotice(null);
    setVoidTarget(null);
    void loadHistory(0, "", "");
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    const next = draftSearch.trim();
    setSearch(next);
    setOffset(0);
    void loadHistory(0, businessDate, next);
  }

  function changeDate(value: string) {
    detailRequestId.current += 1;
    setBusinessDate(value);
    setOffset(0);
    setSelectedOrderId(null);
    setDetail(null);
    setDetailLoading(false);
    setDetailError(null);
    setVoidNotice(null);
    setVoidTarget(null);
    void loadHistory(0, value, search);
  }

  function startVoidReview(kind: VoidKind) {
    const localOrder = history?.orders.find((order) => order.order_id === selectedOrderId) ?? null;
    const currentDetail = detail?.order.order_id === selectedOrderId ? detail : null;
    const orderId = currentDetail?.order.order_id ?? localOrder?.order_id;
    const orderNo = currentDetail?.order.order_no ?? localOrder?.order_no;
    const orderStatus = currentDetail?.order.order_status ?? localOrder?.order_status;
    const amount = currentDetail?.order.grand_total ?? localOrder?.grand_total;
    const currencyCode = currentDetail?.order.currency_code ?? localOrder?.currency_code;
    const candidateAllowed = kind === "CREDIT"
      ? Boolean(history?.permissions.credit_void_candidate || currentDetail?.capabilities.can_void_credit_sale)
      : Boolean(history?.permissions.cash_void_candidate || currentDetail?.capabilities.can_void_cash_sale);

    if (!orderId || !orderNo || amount === undefined || !currencyCode || orderStatus === "VOIDED" || !candidateAllowed) return;

    setVoidKind(kind);
    setVoidTarget({ orderId, orderNo, amount: Number(amount), currencyCode, kind });
    setVoidReason("");
    setVoidError(null);
    setVoidRequestKey(requestKey(orderId, kind));
    setReviewOpen(true);
  }

  async function confirmVoid() {
    if (!terminal || !session || !identity || !voidTarget || !voidKind) return;
    const candidateAllowed = voidKind === "CREDIT"
      ? Boolean(history?.permissions.credit_void_candidate || (detail?.order.order_id === voidTarget.orderId && detail.capabilities.can_void_credit_sale))
      : Boolean(history?.permissions.cash_void_candidate || (detail?.order.order_id === voidTarget.orderId && detail.capabilities.can_void_cash_sale));
    if (!candidateAllowed) return;
    if (!voidReason.trim()) { setVoidError("กรุณาระบุเหตุผลที่ยกเลิกบิล"); return; }
    setVoidBusy(true);
    setVoidError(null);
    setHistoryError(null);
    try {
      const target = voidTarget;
      const key = voidRequestKey || requestKey(target.orderId, voidKind);
      await commitLocalSaleVoid({
        identity,
        performingEmployeeId: session.actor.employee_id,
        orderId: target.orderId,
        orderNo: target.orderNo,
        kind: voidKind,
        reason: voidReason,
        requestKey: key,
        sourceSurface: "SALES_HISTORY",
      });
      setPendingVoidOrderIds((current) => current.includes(target.orderId) ? current : [...current, target.orderId]);
      setReviewOpen(false);
      setVoidKind(null);
      setVoidTarget(null);
      setVoidNotice("ยกเลิกบิลแล้ว");

      if (online) {
        const results = await syncPendingSaleVoids(identity, terminal.deviceSecret, session.sessionToken);
        const ownResult = results.find((result) => result.requestKey === key);
        if (ownResult && !ownResult.ok) {
          if (!isConnectivityError(ownResult.error)) {
            setVoidNotice(friendlyError(ownResult.error ?? "SALE_VOID_SYNC_FAILED"));
          }
          return;
        }
        setVoidNotice("ยกเลิกบิลแล้ว");
        await Promise.all([loadDetail(target.orderId), loadHistory(offset, businessDate, search)]);
      }
    } catch (error) {
      setVoidError(friendlyError(error instanceof Error ? error.message : "SALE_VOID_LOCAL_COMMIT_FAILED"));
    } finally {
      setVoidBusy(false);
    }
  }

  const hasPrevious = offset > 0;
  const hasNext = Boolean(history && offset + history.pagination.returned_count < history.pagination.total_count);
  const selectedOrder = useMemo(
    () => history?.orders.find((order) => order.order_id === selectedOrderId) ?? null,
    [history, selectedOrderId],
  );
  const selectedDetail = detail?.order.order_id === selectedOrderId ? detail : null;
  const selectedPayment = selectedDetail?.payments[0] ?? selectedOrder?.payments[0] ?? null;
  const detailTimeZone = history?.timezone;
  const detailNet = useMemo(() => selectedDetail ? Number(selectedDetail.order.grand_total) - Number(selectedDetail.order.refunded_amount || 0) : 0, [selectedDetail]);
  const selectedReceivable = selectedDetail?.receivable ?? selectedOrder?.receivable ?? null;
  const selectedPaymentCurrency = selectedDetail?.order.currency_code ?? selectedOrder?.currency_code ?? "THB";
  const paymentTenderSummary = !selectedReceivable && selectedPayment ? (
    <div className="pos-history-payment-audit">
      {selectedPayment.tendered_amount != null ? <span><b>รับเงิน</b> {money(selectedPayment.tendered_amount, selectedPaymentCurrency)}</span> : null}
      {selectedPayment.change_amount != null ? <span><b>เงินทอน</b> {money(selectedPayment.change_amount, selectedPaymentCurrency)}</span> : null}
    </div>
  ) : null;
  const selectedOrderStatus = selectedDetail?.order.order_status ?? selectedOrder?.order_status ?? null;
  const selectedVoidKind: VoidKind | null = selectedOrderId ? (selectedReceivable ? "CREDIT" : "CASH") : null;
  const detailCanVoidSelected = selectedVoidKind === "CREDIT"
    ? Boolean(selectedDetail?.capabilities.can_void_credit_sale)
    : selectedVoidKind === "CASH"
      ? Boolean(selectedDetail?.capabilities.can_void_cash_sale)
      : false;
  const localCandidateCanVoidSelected = selectedVoidKind === "CREDIT"
    ? Boolean(history?.permissions.credit_void_candidate)
    : selectedVoidKind === "CASH"
      ? Boolean(history?.permissions.cash_void_candidate)
      : false;
  const voidPending = Boolean(selectedOrderId && pendingVoidOrderIds.includes(selectedOrderId));
  const canVoidSelected = Boolean(
    selectedVoidKind &&
    selectedOrderStatus &&
    selectedOrderStatus !== "VOIDED" &&
    !voidPending &&
    (localCandidateCanVoidSelected || detailCanVoidSelected)
  );
  const voidEvent = useMemo(() => {
    const events = selectedDetail?.events.filter((event) => event.event_type === "ORDER_VOIDED") ?? [];
    return events.reduce<(typeof events)[number] | null>((latest, event) => {
      if (!latest) return event;
      return new Date(event.occurred_at).getTime() > new Date(latest.occurred_at).getTime() ? event : latest;
    }, null);
  }, [selectedDetail]);

  if (!mountNode || !terminal || !actor || !canView) return null;

  return (
    <>
      {createPortal(
        <button className="sales-history-launch" type="button" onClick={openHistory}>
          <span aria-hidden="true">◷</span><strong>ประวัติการขาย</strong>
        </button>,
        mountNode,
      )}

      {open ? createPortal(
        <div className="pos-history-backdrop" role="presentation">
          <section className="pos-history-shell" role="dialog" aria-modal="true" aria-labelledby="pos-history-title">
            <header className="pos-history-header">
              <div>
                <p>SALES HISTORY</p>
                <h2 id="pos-history-title">ประวัติการขาย · {actor.branch_name}</h2>
                <small>แสดงข้อมูลล่าสุดในเครื่อง และอัปเดตให้อัตโนมัติเมื่อเชื่อมต่ออินเทอร์เน็ต</small>
              </div>
              <div className="pos-history-header__actions">
                <span className={online ? "pos-history-online" : "pos-history-offline"}>{online ? "Online" : "Offline"}</span>
                <button type="button" onClick={() => setOpen(false)} aria-label="ปิด">×</button>
              </div>
            </header>

            <div className="pos-history-toolbar">
              <label>
                วันที่
                <span style={{ position: "relative", minHeight: 38, display: "flex", alignItems: "center", padding: "0 34px 0 11px", border: "1px solid rgba(0,0,0,.14)", borderRadius: 10, background: "#fff", overflow: "hidden", whiteSpace: "nowrap" }}>
                  <span aria-hidden="true">{buddhistBusinessDate(businessDate)}</span>
                  <span aria-hidden="true" style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", fontSize: 12 }}>⌄</span>
                  <input
                    aria-label="วันที่"
                    type="date"
                    value={businessDate}
                    onChange={(event) => changeDate(event.target.value)}
                    style={{ position: "absolute", inset: 0, width: "100%", minHeight: 38, opacity: 0, cursor: "pointer" }}
                  />
                </span>
              </label>
              <form onSubmit={submitSearch}>
                <input value={draftSearch} onChange={(event) => setDraftSearch(event.target.value)} maxLength={128} placeholder="ค้นหาเลขบิล สินค้า หรือสมาชิก" />
                <button type="submit">ค้นหา</button>
              </form>
              <span>{history ? `${history.pagination.total_count} บิล` : "—"}</span>
            </div>

            {!online ? <div className="pos-history-notice">ขณะนี้ออฟไลน์ · ยังดูประวัติและยกเลิกบิลได้จากข้อมูลล่าสุดในเครื่อง</div> : null}
            {voidNotice ? <div className="pos-history-notice">{voidNotice}</div> : null}
            {historyError ? <div className="pos-history-error">{historyError}</div> : null}

            <div className="pos-history-layout">
              <aside className="pos-history-list">
                {historyLoading && !history ? <div className="pos-history-empty">กำลังอ่านประวัติการขาย…</div> : null}
                {!historyLoading && history?.orders.length === 0 ? <div className="pos-history-empty">ไม่พบบิลในเงื่อนไขนี้</div> : null}
                {(history?.orders ?? []).map((order) => {
                  const locallyVoided = pendingVoidOrderIds.includes(order.order_id);
                  const cashAudit = cashTenderAudit(order);
                  return (
                    <button key={order.order_id} type="button" className={`pos-history-order ${selectedOrderId === order.order_id ? "is-active" : ""}`} onClick={() => void loadDetail(order.order_id)}>
                      <div><strong>{order.order_no}</strong><span>{dateTime(order.transaction_at, detailTimeZone)}</span></div>
                      <div><span>{itemLabel(order)}</span><b>{money(order.grand_total, order.currency_code)}</b></div>
                      <div><span>{paymentLabel(order)}</span><em data-status={locallyVoided ? "VOIDED" : businessStatusCode(order.order_status, order.receivable)}>{locallyVoided ? "ยกเลิกบิล" : businessStatusLabel(order.order_status, order.receivable)}</em></div>
                      {cashAudit ? (
                        <div className="pos-history-order__cash-audit">
                          <strong>รับเงิน {money(cashAudit.tenderedAmount, order.currency_code)}</strong>
                          {cashAudit.changeAmount != null ? <span>ทอน {money(cashAudit.changeAmount, order.currency_code)}</span> : null}
                        </div>
                      ) : null}
                      {order.customer.customer_name ? <small>สมาชิก · {order.customer.customer_name}</small> : null}
                    </button>
                  );
                })}
                {(history?.orders.length ?? 0) > 0 ? (
                  <div className="pos-history-pagination">
                    <button type="button" disabled={!hasPrevious || historyLoading} onClick={() => void loadHistory(Math.max(0, offset - PAGE_SIZE), businessDate, search)}>ก่อนหน้า</button>
                    <span>{offset + 1}–{offset + (history?.pagination.returned_count ?? 0)}</span>
                    <button type="button" disabled={!hasNext || historyLoading} onClick={() => void loadHistory(offset + PAGE_SIZE, businessDate, search)}>ถัดไป</button>
                  </div>
                ) : null}
              </aside>

              <main className="pos-history-detail">
                {detailLoading && !selectedDetail && !selectedOrder ? <div className="pos-history-empty">กำลังอ่านรายละเอียดบิล…</div> : null}
                {detailError ? <div className="pos-history-error">{detailError}</div> : null}
                {!selectedDetail && !selectedOrder && !detailLoading && !detailError ? <div className="pos-history-empty">เลือกบิลด้านซ้ายเพื่อดูรายละเอียด</div> : null}

                {selectedOrder && !selectedDetail ? (
                  <>
                    {detailLoading ? <div className="pos-history-notice">แสดงข้อมูลล่าสุดทันที · กำลังอัปเดตรายละเอียดเพิ่มเติม</div> : !online ? <div className="pos-history-notice">แสดงข้อมูลล่าสุดที่มีในเครื่อง</div> : null}
                    <section className="pos-history-detail__hero">
                      <div><span data-status={voidPending ? "VOIDED" : businessStatusCode(selectedOrder.order_status, selectedOrder.receivable)}>{voidPending ? "ยกเลิกบิล" : businessStatusLabel(selectedOrder.order_status, selectedOrder.receivable)}</span><h3>{selectedOrder.order_no}</h3><p>{dateTime(selectedOrder.transaction_at, detailTimeZone)}</p></div>
                      <div><small>ยอดบิล</small><strong>{money(selectedOrder.grand_total, selectedOrder.currency_code)}</strong>{selectedOrder.refunded_amount > 0 ? <p>คืนแล้ว {money(selectedOrder.refunded_amount, selectedOrder.currency_code)}</p> : null}</div>
                    </section>

                    <section className="pos-history-facts">
                      <div><small>พนักงาน</small><strong>{selectedOrder.performing_employee.employee_name ?? "—"}</strong></div>
                      <div><small>สมาชิก / ลูกค้า</small><strong>{selectedOrder.customer.customer_name ?? "ไม่ได้ระบุสมาชิก"}</strong>{selectedOrder.customer.membership_status ? <span>{selectedOrder.customer.membership_status}</span> : null}</div>
                      <div className="pos-history-fact--payment"><small>ชำระเงิน</small>{voidPending ? <><strong>{selectedOrder.receivable ? "ชำระภายหลัง" : selectedPayment?.payment_method_name ?? "—"}</strong><span>ยกเลิกบิลแล้ว</span></> : selectedOrder.receivable ? <><strong>ชำระภายหลัง</strong><span>{selectedOrder.receivable.receivable_status === "VOIDED" ? `ยกเลิกบิล · ยอดเดิม ${money(selectedOrder.receivable.original_amount, selectedOrder.receivable.currency_code)}` : `${receivableStatusLabel(selectedOrder.receivable)} · คงเหลือ ${money(selectedOrder.receivable.remaining_amount, selectedOrder.receivable.currency_code)}`}</span></> : <><strong>{selectedPayment?.payment_method_name ?? "—"}</strong><span>{selectedPayment?.payment_status ?? ""}</span></>}{paymentTenderSummary}</div>
                    </section>

                    <section className="pos-history-lines">
                      <h4>สินค้าในบิล</h4>
                      {selectedOrder.items.map((line) => (
                        <div className="pos-history-line" key={line.line_id}>
                          <div><strong>{line.item_name}</strong>{line.variant_name ? <span>{line.variant_name}</span> : null}<small>{line.quantity} รายการ</small></div>
                          <b>{money(line.line_total, selectedOrder.currency_code)}</b>
                        </div>
                      ))}
                    </section>

                    <section className="pos-history-summary">
                      <div className="is-total"><span>รวม</span><strong>{money(selectedOrder.grand_total, selectedOrder.currency_code)}</strong></div>
                    </section>

                    {voidPending ? (
                      <div className="pos-history-voided">
                        <strong>บิลนี้ถูกยกเลิกแล้ว</strong>
                      </div>
                    ) : null}
                  </>
                ) : null}

                {selectedDetail ? (
                  <>
                    <section className="pos-history-detail__hero">
                      <div><span data-status={voidPending ? "VOIDED" : businessStatusCode(selectedDetail.order.order_status, selectedDetail.receivable)}>{voidPending ? "ยกเลิกบิล" : businessStatusLabel(selectedDetail.order.order_status, selectedDetail.receivable)}</span><h3>{selectedDetail.order.order_no}</h3><p>{dateTime(selectedDetail.order.paid_at ?? selectedDetail.order.fulfilled_at ?? selectedDetail.order.created_at, detailTimeZone)}</p></div>
                      <div><small>ยอดบิล</small><strong>{money(selectedDetail.order.grand_total, selectedDetail.order.currency_code)}</strong>{selectedDetail.order.refunded_amount > 0 ? <p>สุทธิหลังคืน {money(detailNet, selectedDetail.order.currency_code)}</p> : null}</div>
                    </section>

                    <section className="pos-history-facts">
                      <div><small>พนักงาน</small><strong>{selectedDetail.order.performing_employee.employee_name ?? "—"}</strong></div>
                      <div><small>สมาชิก / ลูกค้า</small><strong>{selectedDetail.order.customer.customer_name ?? "ไม่ได้ระบุสมาชิก"}</strong>{selectedDetail.order.customer.membership_status ? <span>{selectedDetail.order.customer.membership_status}</span> : null}</div>
                      <div className="pos-history-fact--payment"><small>ชำระเงิน</small>{voidPending ? <><strong>{selectedDetail.receivable ? "ชำระภายหลัง" : selectedPayment?.payment_method_name ?? "—"}</strong><span>ยกเลิกบิลแล้ว</span></> : selectedDetail.receivable ? <><strong>ชำระภายหลัง</strong><span>{selectedDetail.receivable.receivable_status === "VOIDED" ? `ยกเลิกบิล · ยอดเดิม ${money(selectedDetail.receivable.original_amount, selectedDetail.receivable.currency_code)}` : `${receivableStatusLabel(selectedDetail.receivable)} · คงเหลือ ${money(selectedDetail.receivable.remaining_amount, selectedDetail.receivable.currency_code)}`}</span></> : <><strong>{selectedPayment?.payment_method_name ?? "—"}</strong><span>{selectedPayment?.payment_status ?? ""}</span></>}{paymentTenderSummary}</div>
                    </section>

                    <section className="pos-history-lines">
                      <h4>สินค้าในบิล</h4>
                      {selectedDetail.lines.map((line) => (
                        <div className="pos-history-line" key={line.line_id}>
                          <div><strong>{line.item_name}</strong>{line.variant_name ? <span>{line.variant_name}</span> : null}<small>{line.quantity} × {money(line.unit_price, selectedDetail.order.currency_code)}</small>{line.modifiers.map((modifier) => <small key={modifier.modifier_id}>+ {modifier.option_name}{modifier.total_price_delta ? ` ${money(modifier.total_price_delta, selectedDetail.order.currency_code)}` : ""}</small>)}</div>
                          <b>{money(line.line_total, selectedDetail.order.currency_code)}</b>
                        </div>
                      ))}
                    </section>

                    <section className="pos-history-summary">
                      <div><span>สินค้า</span><strong>{money(selectedDetail.order.subtotal_amount, selectedDetail.order.currency_code)}</strong></div>
                      <div><span>ตัวเลือกเพิ่ม</span><strong>{money(selectedDetail.order.modifier_amount, selectedDetail.order.currency_code)}</strong></div>
                      <div><span>ส่วนลด</span><strong>−{money(selectedDetail.order.discount_amount, selectedDetail.order.currency_code)}</strong></div>
                      <div className="is-total"><span>รวม</span><strong>{money(selectedDetail.order.grand_total, selectedDetail.order.currency_code)}</strong></div>
                    </section>

                    {voidPending || selectedDetail.order.order_status === "VOIDED" ? (
                      <div className="pos-history-voided">
                        <strong>บิลนี้ถูกยกเลิกแล้ว</strong>
                        {selectedDetail.order.order_status === "VOIDED" ? <span>เหตุผล: {voidEvent?.reason || "ไม่ได้ระบุเหตุผล"}</span> : null}
                        {selectedDetail.order.order_status === "VOIDED" ? <small>ยกเลิกเมื่อ {dateTime(voidEvent?.occurred_at ?? selectedDetail.order.voided_at, detailTimeZone)}</small> : null}
                      </div>
                    ) : null}
                  </>
                ) : null}

                {(selectedOrder || selectedDetail) && canVoidSelected && selectedVoidKind ? (
                  <button className="pos-history-void" type="button" onClick={() => startVoidReview(selectedVoidKind)}>ยกเลิกบิล</button>
                ) : null}
              </main>
            </div>
          </section>
        </div>,
        document.body,
      ) : null}

      {reviewOpen && voidTarget && voidKind ? createPortal(
        <div className="pos-history-review-backdrop" role="presentation">
          <section className="pos-history-review" role="dialog" aria-modal="true" aria-labelledby="pos-history-void-title">
            <p>ตรวจสอบก่อนยกเลิก</p>
            <h3 id="pos-history-void-title">ยกเลิกบิล {voidTarget.orderNo}</h3>
            <strong>{money(voidTarget.amount, voidTarget.currencyCode)}</strong>
            <div className="pos-history-review__impact">กรุณาตรวจสอบยอดและระบุเหตุผลที่ยกเลิกบิล เมื่อยืนยันแล้วระบบจะยกเลิกบิลและปรับรายการที่เกี่ยวข้องให้อัตโนมัติ</div>
            <label>เหตุผลที่ยกเลิกบิล<input value={voidReason} onChange={(event) => setVoidReason(event.target.value)} maxLength={500} autoFocus placeholder="เช่น ลูกค้าขอยกเลิกรายการ" /></label>
            {voidError ? <div className="pos-history-error">{voidError}</div> : null}
            <div className="pos-history-review__actions">
              <button type="button" disabled={voidBusy} onClick={() => { setReviewOpen(false); setVoidKind(null); setVoidTarget(null); }}>ไม่ยกเลิก</button>
              <button type="button" disabled={voidBusy || !voidReason.trim()} onClick={() => void confirmVoid()}>{voidBusy ? "กำลังบันทึก…" : "ยืนยันยกเลิกบิล"}</button>
            </div>
          </section>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
